import { useEffect, useRef, useState } from "react";
import { htmlToMarkdown, markdownToHtml } from "../../shared/markdown";
import { useDebouncedAutosave, type AutosaveStatus } from "../../shared/useDebouncedAutosave";
import { saveReferenzMd } from "./api";

interface Props {
  /** Latest known markdown body — re-seeded by `file_changed` events. */
  referenzContent: string;
  slug: string | null;
  /** Called when the user clicks "Weiter zum Download". Flushes pending save. */
  onNext: (flush: () => Promise<void>) => void;
}

export function Step2Edit({ referenzContent, slug, onNext }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const lastSeededRef = useRef<string>("");
  const [status, setStatus] = useState<{ kind: AutosaveStatus; message?: string }>({ kind: "idle" });

  const { schedule, flush } = useDebouncedAutosave({
    save: async () => {
      if (!slug || !previewRef.current) return;
      const md = htmlToMarkdown(previewRef.current.innerHTML);
      await saveReferenzMd(slug, md);
    },
    onStatus: (kind, message) => {
      setStatus({ kind, message });
      if (kind === "saved") window.setTimeout(() => setStatus({ kind: "idle" }), 2000);
    },
  });

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (referenzContent === lastSeededRef.current) return;
    lastSeededRef.current = referenzContent;
    el.innerHTML = markdownToHtml(referenzContent);
  }, [referenzContent]);

  function statusText(): string {
    if (status.kind === "saving") return "Speichern…";
    if (status.kind === "saved") return "Gespeichert";
    if (status.kind === "error") return "Fehler: " + (status.message ?? "");
    return "";
  }

  return (
    <section className="wizard-section" data-inqa-step="2">
      <h2 className="form-title">INQA: Referenz prüfen &amp; bearbeiten</h2>
      <p className="form-description">
        Der Agent generiert die Projektreferenz. Du kannst sie direkt bearbeiten – Änderungen
        werden automatisch gespeichert.
      </p>
      <div className="form-actions" style={{ marginBottom: "1rem" }}>
        <span
          className={`autosave-status${status.kind === "saving" ? " saving" : ""}${status.kind === "saved" ? " saved" : ""}`}
          aria-live="polite"
        >
          {statusText()}
        </span>
      </div>
      <div
        ref={previewRef}
        className="result-preview result-preview-editable"
        contentEditable
        spellCheck
        suppressContentEditableWarning
        data-placeholder="Projektreferenz wird generiert…"
        onInput={() => schedule()}
      />
      <div className="form-actions" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onNext(flush)}
          disabled={!slug}
        >
          Weiter zum Download
        </button>
      </div>
    </section>
  );
}
